// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Checkbox, GridItem, Typography } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import TextArea, { TextAreaProps } from "@pacific/MobiusOverrides/TextArea/TextArea";
import React, { ChangeEvent, useContext, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    extendedStyles?: RfqQuoteDetailsLineItemNotesStyles;
}

type Props = OwnProps;

export interface RfqQuoteDetailsLineItemNotesStyles {
    notesGridItem?: GridItemProps;
    notesTextArea?: TextAreaProps;
    // XNG-Changes
    printGridItem?: GridItemProps;
}

const rfqQuoteDetailsLineItemNotesStyles: RfqQuoteDetailsLineItemNotesStyles = {
    notesGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            @media print {
                display: none !important;
            }
        `,
    },
    notesTextArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 54px;
        `,
    },
    // XNG-Changes
    printGridItem: {
        width: 6,
        css: css`
            display: none;
            padding: 0 10px;
            @media print {
                display: block;
            }
        `,
    },
};

const RfqQuoteDetailsLineItemNotes = ({ quote, quoteLine, extendedStyles }: Props) => {
    const styles = rfqQuoteDetailsLineItemNotesStyles;
    const toasterContext = useContext(ToasterContext);

    const [showNotes, setShowNotes] = useState(false);
    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const [notes, setNotes] = useState(quoteLine.notes);

    const notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };

    const notesBlurHandler = () => {
        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            quoteLine: {
                ...quoteLine,
                notes,
            },
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Notes updated"), messageType: "success" });
            },
            onComplete(resultProps: any) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const toggleNotesHeading = showNotes
        ? translate("Hide Line Notes")
        : quoteLine.notes.length === 0
        ? translate("Add Line Notes")
        : translate("Show Line Notes");

    return (
        <>
            <GridItem {...styles.notesGridItem} {...{ showNotes }}>
                <Checkbox checked={showNotes} onChange={toggleNotesClickHandler} aria-label={toggleNotesHeading}>
                    {translate(toggleNotesHeading)}
                </Checkbox>
                {showNotes && (
                    <TextArea
                        {...styles.notesTextArea}
                        value={notes}
                        disabled={!quote.canModifyOrder}
                        onChange={notesChangeHandler}
                        onBlur={notesBlurHandler}
                        placeholder={translate("Please add any additional information for this product here...")}
                        label={translate("Line Note")}
                    />
                )}
            </GridItem>
            {/* XNG-Changes: for print view */}
            {notes && (
                <GridItem {...styles.printGridItem}>
                    <Typography weight="bold">{translate("Notes")}:</Typography>
                    <Typography>{notes}</Typography>
                </GridItem>
            )}
        </>
    );
};

export default RfqQuoteDetailsLineItemNotes;
